/**
 * Cross-app SSO between ReachOut and Wizetok via short-lived handoff JWT.
 * Prefer the ReachOut /sso/wizetok bridge so the browser sends ReachOut cookies
 * and Wizetok receives a handoff token — no second login when already signed in.
 */
import api from "../services/axios";

function isLocalHost(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

export function getWizetokBaseUrl() {
  const fromEnv = (process.env.REACT_APP_WIZETOK_URL || "").trim().replace(/\/$/, "");
  if (fromEnv) return fromEnv;

  const { protocol, hostname } = window.location;
  if (isLocalHost(hostname)) return "http://localhost:3025";
  if (hostname.includes("reachout")) {
    return `${protocol}//${hostname.replace(/reachout/i, "wizetok")}`;
  }
  return "https://wizetok.hirecrux.ai";
}

export function buildWizetokLoginUrl({ handoffToken, email } = {}) {
  const base = `${getWizetokBaseUrl()}/admin/login`;
  const params = new URLSearchParams();
  if (handoffToken) params.set("handoff", handoffToken);
  if (email) params.set("login_hint", email);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Same-origin bridge — uses ReachOut session cookies, then redirects into Wizetok. */
export function getWizetokBridgeUrl() {
  return `${window.location.origin}/sso/wizetok`;
}

/**
 * Open Wizetok in a new tab during the click (popup-safe).
 * `from=reachout` tells Wizetok not to load ReachOut in an iframe — that iframe
 * was taking over the tab on the first click. Handoff is applied in the same tab
 * if mint succeeds.
 */
export function openWizetokInNewTab() {
  const url = `${getWizetokBaseUrl()}/admin/login?from=reachout`;
  const win = window.open(url, "_blank");
  if (!win) {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return false;
  }
  try {
    win.opener = null;
  } catch {
    /* ignore */
  }
  void (async () => {
    try {
      const { data } = await api.post("/auth/cross-app-handoff", { target: "wizetok" });
      const token = data?.handoffToken;
      if (!token || win.closed) return;
      win.location.replace(buildWizetokLoginUrl({ handoffToken: token }));
    } catch (e) {
      console.error("[cross-app] handoff mint failed", e?.response?.status, e?.message || e);
    }
  })();
  return true;
}

/**
 * Open Wizetok with token exchange.
 * From ReachOut UI: go through /sso/wizetok (most reliable).
 * Fallback: mint handoff JWT here and open Wizetok with ?handoff=.
 */
export async function openWizetokSso(email) {
  openWizetokInNewTab();
}

/** Used by the bridge page itself to mint + redirect. */
export async function mintAndOpenWizetok(email) {
  try {
    const { data } = await api.post("/auth/cross-app-handoff", { target: "wizetok" });
    const token = data?.handoffToken;
    if (token) {
      window.location.replace(buildWizetokLoginUrl({ handoffToken: token, email }));
      return true;
    }
  } catch (e) {
    console.error("[cross-app] handoff mint failed", e?.response?.status, e?.message || e);
  }
  return false;
}

export function userEmailFromAuth(user) {
  if (!user) return "";
  return (
    (user.email || user.mail || user.userPrincipalName || user.upn || "").trim()
  );
}
